import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { patientReminderService } from '../services/patientReminderService'
import type { PatientReminder, PatientReminderFormData, ReminderDeliveryMethod } from '../types/patientReminder'
import { useAuth } from '../contexts/AuthContext'
import './PatientReminders.css'

interface PatientRemindersProps {
  patientId: string
}

const defaultForm: PatientReminderFormData = {
  patientId: '',
  title: '',
  message: '',
  deliveryMethod: 'sms',
  dueDate: '',
}

export function PatientReminders({ patientId }: PatientRemindersProps) {
  const { hasPermission } = useAuth()
  const queryClient = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [formData, setFormData] = useState<PatientReminderFormData>({
    ...defaultForm,
    patientId,
  })

  const canRead = hasPermission('reminders:read') || hasPermission('*')
  const canWrite = hasPermission('reminders:write') || hasPermission('*')

  const { data, isLoading, error } = useQuery<PatientReminder[]>({
    queryKey: ['patient-reminders', patientId],
    queryFn: () => patientReminderService.getPatientReminders(patientId),
    enabled: !!patientId && canRead,
  })
  const reminders = data ?? []

  useEffect(() => {
    if (error) {
      console.error('[PatientReminders] Failed to load reminders:', { patientId, error })
    }
  }, [error, patientId])

  const createMutation = useMutation({
    mutationFn: (data: PatientReminderFormData) => patientReminderService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient-reminders', patientId] })
      setShowForm(false)
      setFormData({ ...defaultForm, patientId })
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<PatientReminderFormData> & { status?: string } }) =>
      patientReminderService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient-reminders', patientId] })
    },
  })

  if (!canRead) {
    return (
      <div className="patient-reminders">
        <div className="error" role="alert">
          You do not have permission to view reminders.
        </div>
      </div>
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const dueDateIso = formData.dueDate ? new Date(formData.dueDate).toISOString() : ''
    await createMutation.mutateAsync({ ...formData, dueDate: dueDateIso })
  }

  return (
    <div className="patient-reminders">
      <div className="reminders-header">
        <div>
          <h2>Patient Reminders</h2>
          <p className="subtitle">Schedule SMS reminders and recalls for this patient.</p>
        </div>
        {canWrite && (
          <button className="btn-primary" onClick={() => setShowForm((prev) => !prev)}>
            {showForm ? 'Close' : '+ New Reminder'}
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="reminder-form">
          <div className="form-group">
            <label htmlFor="reminder-title">Title</label>
            <input
              id="reminder-title"
              type="text"
              value={formData.title}
              onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="reminder-message">Message</label>
            <textarea
              id="reminder-message"
              value={formData.message}
              onChange={(e) => setFormData((prev) => ({ ...prev, message: e.target.value }))}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="reminder-delivery">Delivery Method</label>
            <select
              id="reminder-delivery"
              value={formData.deliveryMethod}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, deliveryMethod: e.target.value as ReminderDeliveryMethod }))
              }
            >
              <option value="sms">SMS</option>
              <option value="email">Email</option>
              <option value="in-app">In-app</option>
              <option value="all">All</option>
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="reminder-due-date">Due date & time</label>
            <input
              id="reminder-due-date"
              type="datetime-local"
              value={formData.dueDate}
              onChange={(e) => setFormData((prev) => ({ ...prev, dueDate: e.target.value }))}
              required
            />
          </div>
          <div className="form-actions">
            <button type="submit" className="btn-primary" disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Saving...' : 'Save Reminder'}
            </button>
          </div>
        </form>
      )}

      {isLoading && <div className="loading">Loading reminders...</div>}
      {error && <div className="error" role="alert">Failed to load reminders.</div>}

      {!isLoading && reminders.length === 0 && (
        <div className="empty-state">No reminders scheduled yet.</div>
      )}

      {!isLoading && reminders.length > 0 && (
        <table className="reminders-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Due</th>
              <th>Status</th>
              <th>Delivery</th>
              <th>Notes</th>
              {canWrite && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {reminders.map((reminder) => (
              <tr key={reminder.id}>
                <td>
                  <div className="reminder-title">{reminder.title}</div>
                  <div className="reminder-message">{reminder.message}</div>
                </td>
                <td>{new Date(reminder.dueDate).toLocaleString()}</td>
                <td>{reminder.status}</td>
                <td>{reminder.deliveryMethod}</td>
                <td>{reminder.deliveryDetails?.error || '-'}</td>
                {canWrite && (
                  <td>
                    {reminder.status === 'pending' && (
                      <button
                        className="btn-secondary btn-small"
                        onClick={() =>
                          updateMutation.mutate({ id: reminder.id, data: { status: 'cancelled' } })
                        }
                      >
                        Cancel
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}
